import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, FC, useRef, useEffect } from "react";
import { ClassNamesArg, css, cx } from "@/domains/emotion";
import { MdsIconKind } from "@/design-system/components/icon/types";
import { iconDefinitionMapping } from "@/design-system/components/icon/iconDefinitionMapping";
import { mdsColors } from "@/design-system/foundations";
import cloudSlashFlippedImage from "@/assets/images/cloud-slash-flipped.svg";
import codeBlockImage from "@/assets/images/code-block.svg";
import zapierLogoImage from "@/assets/icons/brands/zapier.svg";
import zapierLogoSquareImage from "@/assets/icons/brands/zapier-square.svg";
import firefliesLogoImage from "@/assets/icons/brands/fireflies.svg";
import typeformLogoImage from "@/assets/icons/brands/typeform.svg";
import skipForwardImage from "@/assets/icons/other/skip-forward.svg";
import skipBackwardImage from "@/assets/icons/other/skip-backward.svg";

export interface MdsIconStylingProps {
  className?: ClassNamesArg;
  innerStyles?: {
    Icon?: {
      className?: ClassNamesArg;
    };
  };
}

export interface MdsIconProps extends MdsIconStylingProps {
  kind: MdsIconKind;
}

// Will override FA icons
const customIcons: Partial<
  Record<MdsIconKind, { image: string; sizeRatio?: number; borderRadius?: string }>
> = {
  [MdsIconKind.CloudSlashFlipped]: { image: cloudSlashFlippedImage },
  [MdsIconKind.CodeBlock]: { image: codeBlockImage },
  [MdsIconKind.LogoZapier]: { image: zapierLogoImage, sizeRatio: 1.3, borderRadius: "20%" },
  [MdsIconKind.LogoZapierSquare]: {
    image: zapierLogoSquareImage,
    sizeRatio: 0.8,
    borderRadius: "20%",
  },
  [MdsIconKind.LogoFireflies]: { image: firefliesLogoImage, borderRadius: "20%" },
  [MdsIconKind.LogoTypeform]: { image: typeformLogoImage, borderRadius: "20%" },
  [MdsIconKind.SkipForward]: { image: skipForwardImage },
  [MdsIconKind.SkipBackward]: { image: skipBackwardImage },
};

export const MdsIcon: FC<MdsIconProps> = memo(({ kind, className, innerStyles }) => {
  return (
    <div className={cx(wrapperStyles, className)}>
      <Icon kind={kind} innerStyles={innerStyles} />
    </div>
  );
});

const Icon = ({ kind, innerStyles }: MdsIconProps) => {
  const iconDefinition = iconDefinitionMapping[kind];
  const { style, ref } = useSpinnerAnimation({ kind });

  if (kind in customIcons) {
    const iconInfo = customIcons[kind]!;

    return (
      <img
        ref={ref as React.RefObject<HTMLImageElement>}
        src={iconInfo.image}
        style={style}
        className={cx(
          fsStyles,
          css({
            width: 18 * (iconInfo.sizeRatio ?? 1),
            height: 18 * (iconInfo.sizeRatio ?? 1),
            borderRadius: iconInfo.borderRadius ?? 0,
          }),
          innerStyles?.Icon?.className
        )}
      />
    );
  }

  return (
    <FontAwesomeIcon
      ref={ref as React.RefObject<SVGSVGElement>}
      icon={iconDefinition}
      className={cx(fsStyles, innerStyles?.Icon?.className)}
      style={style}
    />
  );
};

const wrapperStyles = css({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  height: 16,
  width: 16,
  color: mdsColors().grey.x700, // Applied here to allow .mds-btn-icon to override style
});

const fsStyles = css({
  fontSize: "14px",
  lineHeight: 1,
  userSelect: "none",
  draggable: "false",
});

/**
 * Hook that provides unified spinner animation styles that work in both regular DOM and shadow DOM.
 *
 * Detects shadow DOM context and injects keyframes into the appropriate root, then uses
 * inline styles to ensure animations work consistently across all rendering contexts.
 */
const useSpinnerAnimation = ({ kind }: { kind: MdsIconKind }) => {
  const isSpinner = kind === MdsIconKind.Spinner || kind === MdsIconKind.SpinnerSolid;
  const ref = useRef<HTMLImageElement | SVGSVGElement>(null);

  useEffect(() => {
    if (!isSpinner || !ref.current) return;

    const root = ref.current.getRootNode();
    const isShadowRoot = root instanceof ShadowRoot;
    const target = isShadowRoot ? root : document.head;
    const keyframesId = "mds-icon-spin-keyframes";

    // Check if keyframes already exist in the target
    const existingStyle = target.querySelector(`#${keyframesId}`);
    if (!existingStyle) {
      const style = document.createElement("style");
      style.id = keyframesId;
      style.textContent = `
        @keyframes mds-icon-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      target.appendChild(style);
    }
  }, [isSpinner]);

  const style = isSpinner
    ? {
        animation: "mds-icon-spin 1s linear infinite",
        transformOrigin: "center center",
      }
    : undefined;

  return { isSpinner, style, ref };
};
